import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Timer, ChevronLeft, ChevronRight, CheckCircle2, Loader2, WifiOff, AlertTriangle, ShieldCheck } from "lucide-react";
import { API } from "@/lib/contact";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-slate-400 outline-none focus:border-electric focus:ring-2 focus:ring-electric/15 transition-all";

const darkInputCls =
  "w-full rounded-xl border border-white/20 bg-navy-700 px-4 py-3.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-electric focus:ring-2 focus:ring-electric/25 transition-all caret-white";

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

export default function TestPage() {
  const [phase, setPhase] = useState("access"); // access | exam | submitted
  const [access, setAccess] = useState({ student_id: "", mobile: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});
  const [visited, setVisited] = useState(new Set([0]));
  const [remaining, setRemaining] = useState(0);
  const [saveState, setSaveState] = useState("saved"); // saved | saving | offline
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const pendingRef = useRef({});
  const timerRef = useRef(null);

  useEffect(() => {
    document.title = "Scholarship Test | LaunchX Academy";
    return () => clearInterval(timerRef.current);
  }, []);

  const startTest = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/scholarship/test/start`, access);
      const expiresAt = new Date(data.expires_at).getTime();
      const serverOffset = expiresAt - Date.now();
      setExam({ ...data, expiresAt, offset: serverOffset });
      setAnswers(data.answers || {});
      setRemaining(Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)));
      setPhase("exam");
      timerRef.current = setInterval(() => {
        const left = Math.floor((expiresAt - Date.now()) / 1000);
        setRemaining(Math.max(0, left));
        if (left <= 0) {
          clearInterval(timerRef.current);
          doSubmit(data.token, true);
        }
      }, 1000);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Could not start the test. Please check your details or contact staff.");
    } finally {
      setLoading(false);
    }
  };

  const flushPending = async () => {
    const pending = { ...pendingRef.current };
    for (const [qid, sel] of Object.entries(pending)) {
      try {
        await axios.post(`${API}/scholarship/test/answer`, { token: exam.token, question_id: qid, selected: sel });
        delete pendingRef.current[qid];
        setSaveState("saved");
      } catch (err) {
        if (err?.response?.status === 410) {
          clearInterval(timerRef.current);
          doSubmit(exam.token, true);
          return;
        }
        setSaveState("offline");
      }
    }
  };

  const selectOption = (qid, label) => {
    if (submitted) return;
    setAnswers((a) => ({ ...a, [qid]: label }));
    pendingRef.current[qid] = label;
    setSaveState("saving");
    axios.post(`${API}/scholarship/test/answer`, { token: exam.token, question_id: qid, selected: label })
      .then(() => { delete pendingRef.current[qid]; setSaveState("saved"); })
      .catch((err) => {
        if (err?.response?.status === 410) { clearInterval(timerRef.current); doSubmit(exam.token, true); }
        else { setSaveState("offline"); setTimeout(flushPending, 4000); }
      });
  };

  const clearResponse = (qid) => {
    setAnswers((a) => { const n = { ...a }; delete n[qid]; return n; });
    pendingRef.current[qid] = null;
    axios.post(`${API}/scholarship/test/answer`, { token: exam.token, question_id: qid, selected: null }).catch(() => setSaveState("offline"));
  };

  const doSubmit = async (token, auto = false) => {
    if (submitted) return;
    try {
      const { data } = await axios.post(`${API}/scholarship/test/submit`, { token });
      setSubmitted({ ...data, auto });
    } catch (err) {
      if (err?.response?.status === 410 || err?.response?.status === 409) {
        setSubmitted({ student_id: exam.student_id, submission_id: exam.attempt_id, auto: true });
      } else {
        toast.error("Connection issue. Please wait — your answers are saved. Retrying...");
        setTimeout(() => doSubmit(token, auto), 4000);
        return;
      }
    }
    setPhase("submitted");
    clearInterval(timerRef.current);
  };

  const goTo = (i) => {
    setCurrent(i);
    setVisited((v) => new Set([...v, i]));
  };

  const answeredCount = Object.values(answers).filter(Boolean).length;

  /* ---------- ACCESS ---------- */
  if (phase === "access") {
    return (
      <div className="min-h-screen bg-navy flex flex-col" data-testid="test-access-page">
        <header className="border-b border-white/5">
          <div className="max-w-5xl mx-auto px-4 h-[64px] flex items-center justify-between">
            <img src="/logo-full.png" alt="LaunchX Academy" className="h-7 w-auto" />
            <span className="text-[11px] font-bold tracking-[0.2em] text-cyanx uppercase">Scholarship Test</span>
          </div>
        </header>
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <div className="w-full max-w-md bg-navy-800 border border-white/10 rounded-3xl p-7 sm:p-9">
            <h1 className="font-display font-bold text-white text-xl">Start Your Test</h1>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">Enter your Student ID and registered mobile number. The test can only be started when enabled by LaunchX staff at your test centre.</p>
            <form onSubmit={startTest} className="mt-6 space-y-4">
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Student ID</span>
                <input data-testid="test-student-id-input" required value={access.student_id}
                  onChange={(e) => setAccess({ ...access, student_id: e.target.value.toUpperCase() })}
                  placeholder="LXST-2026-000001" className={darkInputCls} />
              </div>
              <div>
                <span className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Registered Mobile Number</span>
                <input data-testid="test-mobile-input" required type="tel" value={access.mobile}
                  onChange={(e) => setAccess({ ...access, mobile: e.target.value })}
                  placeholder="10-digit mobile" className={darkInputCls} />
              </div>
              {error && <p data-testid="test-access-error" className="text-xs font-semibold text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5">{error}</p>}
              <button data-testid="test-start-button" type="submit" disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-electric hover:bg-electric-dark disabled:opacity-60 text-white font-semibold text-sm rounded-xl px-7 py-4 transition-all">
                {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                {loading ? "Checking..." : "Start Test"}
              </button>
            </form>
            <p className="mt-5 text-[11px] text-slate-500 flex items-center gap-1.5"><ShieldCheck size={12} className="text-cyanx" /> Your progress is saved securely on our servers.</p>
          </div>
        </div>
      </div>
    );
  }

  /* ---------- SUBMITTED (confirmation only — never any result) ---------- */
  if (phase === "submitted") {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-4" data-testid="test-submitted-page">
        <div className="max-w-lg w-full bg-navy-800 border border-white/10 rounded-3xl p-8 sm:p-10 text-center">
          <span className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center">
            <CheckCircle2 size={30} className="text-emerald-400" />
          </span>
          <h1 className="mt-6 font-display font-bold text-white text-2xl tracking-wide">TEST SUCCESSFULLY SUBMITTED</h1>
          <p className="mt-4 text-sm text-slate-400 leading-relaxed">
            Thank you for participating in the LaunchX Business &amp; Digital Skills Scholarship Test. Your assessment has been submitted successfully.
          </p>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            Your result, performance report and scholarship eligibility will be reviewed by the LaunchX Academy team and communicated by our staff during the counselling process.
          </p>
          <div className="mt-7 bg-navy rounded-2xl p-5 text-left space-y-2.5">
            <p className="text-sm text-slate-300">Student ID: <span className="font-display font-bold text-cyanx">{submitted?.student_id}</span></p>
            <p className="text-sm text-slate-300">Submission ID: <span className="font-display font-bold text-white">{submitted?.submission_id}</span></p>
            {submitted?.auto && <p className="text-xs text-amber-400">Your test was submitted automatically when the time ended.</p>}
          </div>
          <p className="mt-4 text-xs text-slate-500">Please keep your registered mobile number available for further communication.</p>
          <Link to="/" data-testid="test-home-link" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-cyanx hover:text-white transition-colors">
            Return to LaunchX Home
          </Link>
        </div>
      </div>
    );
  }

  /* ---------- EXAM ---------- */
  const q = exam.questions[current];
  const total = exam.questions.length;
  const progress = Math.round((answeredCount / total) * 100);
  const lowTime = remaining <= 300;

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="exam-page">
      <header className="bg-navy border-b border-white/5 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 h-[60px] flex items-center justify-between gap-3">
          <img src="/logo-full.png" alt="LaunchX Academy" className="h-6 w-auto hidden sm:block" />
          <div className="min-w-0">
            <p className="text-white text-sm font-semibold truncate" data-testid="exam-student-name">{exam.student_name}</p>
            <p className="text-[10px] text-slate-400 truncate">{exam.student_id} · {exam.session_name}</p>
          </div>
          <div className={`flex items-center gap-2 rounded-full px-4 py-2 font-display font-bold text-sm ${lowTime ? "bg-red-500/15 text-red-400 animate-pulse" : "bg-electric/15 text-cyanx"}`} data-testid="exam-timer">
            <Timer size={15} /> {fmt(remaining)}
          </div>
        </div>
        <div className="h-1 bg-white/5"><div className="h-full bg-gradient-to-r from-electric to-cyanx transition-[width] duration-500" style={{ width: `${progress}%` }} /></div>
      </header>

      <div className="flex-1 max-w-6xl w-full mx-auto px-4 py-6 grid lg:grid-cols-[1fr_260px] gap-6">
        <div>
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400" data-testid="exam-question-number">Question {current + 1} of {total}</p>
            <p className="text-xs text-slate-400 flex items-center gap-1.5" data-testid="exam-save-state">
              {saveState === "saving" && <><Loader2 size={12} className="animate-spin" /> Saving...</>}
              {saveState === "saved" && <><CheckCircle2 size={12} className="text-emerald-500" /> Saved</>}
              {saveState === "offline" && <><WifiOff size={12} className="text-amber-500" /> Connection interrupted. Your saved responses are safe. Reconnecting...</>}
            </p>
          </div>
          <div className="mt-4 bg-white border border-slate-200/80 rounded-2xl p-6 sm:p-8" data-testid="exam-question-card">
            <p className="font-display font-semibold text-navy text-lg leading-relaxed">{q.text}</p>
            <div className="mt-6 space-y-3">
              {q.options.map((o) => {
                const selected = answers[q.qid] === o.label;
                return (
                  <button
                    key={o.label}
                    data-testid={`exam-option-${o.label}`}
                    onClick={() => selectOption(q.qid, o.label)}
                    className={`w-full flex items-center gap-4 text-left border rounded-xl px-5 py-4 transition-all min-h-[56px] ${
                      selected ? "border-electric bg-electric/5 ring-2 ring-electric/20" : "border-slate-200 hover:border-electric/40 bg-white"
                    }`}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-sm shrink-0 ${
                      selected ? "bg-electric text-white" : "bg-slate-100 text-slate-500"
                    }`}>{o.label}</span>
                    <span className={`text-sm sm:text-base ${selected ? "text-navy font-medium" : "text-slate-600"}`}>{o.text}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between gap-3">
            <button data-testid="exam-prev-button" onClick={() => goTo(Math.max(0, current - 1))} disabled={current === 0}
              className="inline-flex items-center gap-1.5 border border-slate-300 text-navy font-semibold text-sm rounded-full px-5 py-3.5 disabled:opacity-40 hover:border-electric transition-colors">
              <ChevronLeft size={16} /> Previous
            </button>
            <button data-testid="exam-clear-button" onClick={() => clearResponse(q.qid)}
              className="text-xs font-semibold text-slate-400 hover:text-red-500 transition-colors">
              Clear Response
            </button>
            {current < total - 1 ? (
              <button data-testid="exam-next-button" onClick={() => goTo(current + 1)}
                className="inline-flex items-center gap-1.5 bg-electric hover:bg-electric-dark text-white font-semibold text-sm rounded-full px-6 py-3.5 transition-colors">
                Save &amp; Next <ChevronRight size={16} />
              </button>
            ) : (
              <button data-testid="exam-submit-open-button" onClick={() => setConfirmSubmit(true)}
                className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-800 text-white font-semibold text-sm rounded-full px-6 py-3.5 transition-colors">
                Submit Test
              </button>
            )}
          </div>
        </div>

        <aside className="bg-white border border-slate-200/80 rounded-2xl p-5 h-fit lg:sticky lg:top-[84px]" data-testid="exam-palette">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Questions</p>
          <div className="mt-4 grid grid-cols-5 lg:grid-cols-5 gap-2">
            {exam.questions.map((qq, i) => {
              const answered = !!answers[qq.qid];
              const isCurrent = i === current;
              return (
                <button key={qq.qid} data-testid={`palette-${i + 1}`} onClick={() => goTo(i)}
                  className={`aspect-square rounded-lg text-xs font-bold transition-all ${
                    isCurrent ? "bg-navy text-white ring-2 ring-electric" :
                    answered ? "bg-electric text-white" :
                    visited.has(i) ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                  }`}>
                  {i + 1}
                </button>
              );
            })}
          </div>
          <div className="mt-4 space-y-1.5 text-[11px] text-slate-500">
            <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-electric inline-block" /> Answered</p>
            <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-amber-100 inline-block" /> Visited</p>
            <p className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-slate-100 inline-block" /> Not Answered</p>
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100">
            <p className="text-xs text-slate-500">Answered <span className="font-bold text-navy">{answeredCount}</span> of {total} · {progress}%</p>
            <button data-testid="exam-submit-button" onClick={() => setConfirmSubmit(true)}
              className="mt-3 w-full bg-navy hover:bg-navy-800 text-white font-semibold text-sm rounded-xl px-5 py-3.5 transition-colors">
              Submit Test
            </button>
          </div>
        </aside>
      </div>

      {confirmSubmit && (
        <div className="fixed inset-0 z-50 bg-navy/70 backdrop-blur-sm flex items-center justify-center px-4" data-testid="exam-submit-modal">
          <div className="bg-white rounded-3xl p-7 sm:p-9 max-w-md w-full">
            <AlertTriangle size={28} className="text-amber-500" />
            <h3 className="mt-4 font-display font-bold text-navy text-xl">Submit your test?</h3>
            <p className="mt-3 text-sm text-slate-500 leading-relaxed">You have answered {answeredCount} of {total} questions.</p>
            {total - answeredCount > 0 && (
              <p className="mt-2 text-sm font-semibold text-amber-600">You still have {total - answeredCount} unanswered question{total - answeredCount > 1 ? "s" : ""}. Are you sure you want to submit?</p>
            )}
            <p className="mt-2 text-xs text-slate-400">Once submitted, answers cannot be changed.</p>
            <div className="mt-6 flex gap-3">
              <button data-testid="exam-review-button" onClick={() => setConfirmSubmit(false)}
                className="flex-1 border border-slate-300 text-navy font-semibold text-sm rounded-xl px-5 py-3.5 hover:border-electric transition-colors">
                Review Questions
              </button>
              <button data-testid="exam-confirm-submit-button" onClick={() => { setConfirmSubmit(false); doSubmit(exam.token); }}
                className="flex-1 bg-electric hover:bg-electric-dark text-white font-semibold text-sm rounded-xl px-5 py-3.5 transition-colors">
                Submit Test
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
